import { Dialog, useDialogManager } from '../../../../../../widgets/dialog';
import { Icon } from '../../../../../../widgets/icon';
import { AdvancedSelect } from '../../../../../../widgets/select';
import Stack from '../../../../../../widgets/stack/stack';
import React from 'react';
import { T } from 'src/shared/helpers';
import ArrowIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import MaterialIcon from 'src/shared/material_icons/image/texture.svg';
import ObjectIcon from 'src/shared/material_icons/maps/layers.svg';
import styled from 'styled-components';
import { selectionSurface, SelectionItemName } from './composerSelectionLayout';

interface ComposerRetargetAttributeOutput {
    // Mapping from old object/material id to new object/material id
    objectMap?: Record<string, string>;
    materialMap?: Record<string, string>;
}
interface ComposerRetargetAttributeDialogProps {
    objectsList: BABYLON.AbstractMesh[];
    materialsList: BABYLON.Material[];
    usedMaterialsList: BABYLON.Material[];
    usedObjectsList: BABYLON.AbstractMesh[];
    onConfirm: (result: ComposerRetargetAttributeOutput) => void;
}

// Una riga per cosa da rimappare: a sinistra quello che le azioni usano oggi, a destra quello che
// useranno. La freccia in mezzo dice il verso, che in due colonne affiancate non si darebbe per
// scontato — «Shell → Shell_2» e «Shell_2 → Shell» sono due cose diverse.
const MATCH_COLUMNS = 'minmax(0, 1fr) 20px minmax(0, 1.1fr)';

const GroupTitle = styled.h3`
    margin: 0;
    color: var(--composer-muted, #455a64);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const MatchHeader = styled.div`
    display: grid;
    grid-template-columns: ${MATCH_COLUMNS};
    gap: 8px;
    padding: 0 8px 2px;
    color: var(--composer-faint, #78909c);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const MatchRow = styled.div<{ $selected: boolean }>`
    ${selectionSurface}
    display: grid;
    grid-template-columns: ${MATCH_COLUMNS};
    align-items: center;
    gap: 8px;
    padding: 8px;
`;

const Current = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;

    ${Icon} {
        flex: 0 0 16px;
        color: var(--composer-muted, #455a64);
    }
`;

const Arrow = styled(Icon)`
    color: var(--composer-faint, #78909c);
`;

const Group = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Rows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Intro = styled.p`
    margin: 0;
    color: var(--composer-muted, #455a64);
    font-size: 12px;
    line-height: 18px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 24px 28px;
    color: var(--composer-muted, #455a64);
    text-align: center;

    p {
        max-width: 34ch;
        margin: 0;
        font-size: 13px;
        line-height: 1.6;
    }

    ${Icon} {
        width: 26px;
        height: 26px;
        opacity: 0.5;
    }
`;

const ComposerRetargetAttributeDialog: React.FC<ComposerRetargetAttributeDialogProps> = ({
    materialsList: sceneMaterialList,
    objectsList: sceneMeshList,
    usedMaterialsList: materialsList,
    usedObjectsList: objectsList,
    onConfirm
}) => {
    const { filteredMaterialList, filteredObjectList } = React.useMemo(() => {
        const matMap: { [id: string]: BABYLON.Material } = {};
        materialsList.forEach((mat) => {
            matMap[mat.id] = mat;
        });
        const objMap: { [id: string]: BABYLON.AbstractMesh } = {};
        objectsList.forEach((obj) => {
            objMap[obj.id] = obj;
        });
        return { filteredMaterialList: Object.values(matMap), filteredObjectList: Object.values(objMap) };
    }, [materialsList, objectsList]);
    const [materialMap, setMaterialMap] = React.useState<{ [oldId: string]: string }>({});
    const [objectMap, setObjectMap] = React.useState<{ [oldId: string]: string }>({});
    const { currentDialogId, closeDialog } = useDialogManager();

    const hasSomethingToMatch = filteredMaterialList.length > 0 || filteredObjectList.length > 0;
    const matchedCount = Object.keys(materialMap).length + Object.keys(objectMap).length;

    const handleConfirm = () => {
        const result: ComposerRetargetAttributeOutput = {
            materialMap: materialMap,
            objectMap: objectMap
        };
        onConfirm(result);
    };

    const setMatch = (setMap: React.Dispatch<React.SetStateAction<{ [oldId: string]: string }>>, oldId: string, newId?: string) =>
        setMap((old) => {
            if (!newId) {
                const next = { ...old };
                delete next[oldId];
                return next;
            }
            return { ...old, [oldId]: newId };
        });

    return (
        <Dialog
            size='small'
            title={T._('Match objects and materials', 'Admin')}
            /* Senza niente da rimappare non c'è niente da confermare: resta il solo comando che
               questa finestra può ancora onorare, cioè chiuderla. */
            buttons={
                hasSomethingToMatch
                    ? [{ label: T._('Confirm', 'Admin'), disabled: matchedCount === 0, onClick: handleConfirm }]
                    : [{ label: T._('Close', 'Admin'), outline: true, onClick: () => closeDialog(currentDialogId) }]
            }
        >
            {hasSomethingToMatch ? (
                <Stack $gap='16px'>
                    <Intro>
                        {T._('Choose the scene object or material that replaces the one the actions use today. What you leave empty stays as it is.', 'Admin')}
                    </Intro>

                    {filteredObjectList.length > 0 && (
                        <Group>
                            <GroupTitle>{T._('Objects', 'Admin')}</GroupTitle>
                            <MatchHeader aria-hidden='true'>
                                <span>{T._('Used now', 'Admin')}</span>
                                <span />
                                <span>{T._('Replace with', 'Admin')}</span>
                            </MatchHeader>
                            <Rows>
                                {filteredObjectList.map((obj) => (
                                    <MatchRow key={obj.id} $selected={!!objectMap[obj.id]}>
                                        <Current>
                                            <Icon>
                                                <ObjectIcon />
                                            </Icon>
                                            <SelectionItemName title={obj.name}>{obj.name}</SelectionItemName>
                                        </Current>
                                        <Arrow aria-hidden='true'>
                                            <ArrowIcon />
                                        </Arrow>
                                        <AdvancedSelect
                                            aria-label={T._(`Replace ${obj.name}`, 'Admin')}
                                            options={sceneMeshList.filter((o) => o.id !== obj.id).map((o) => ({ label: o.name, value: o.id }))}
                                            placeholder={T._('Leave as it is', 'Admin')}
                                            isClearable
                                            menuPosition='fixed'
                                            onChange={(sel) => setMatch(setObjectMap, obj.id, sel?.value)}
                                        />
                                    </MatchRow>
                                ))}
                            </Rows>
                        </Group>
                    )}

                    {filteredMaterialList.length > 0 && (
                        <Group>
                            <GroupTitle>{T._('Materials', 'Admin')}</GroupTitle>
                            <MatchHeader aria-hidden='true'>
                                <span>{T._('Used now', 'Admin')}</span>
                                <span />
                                <span>{T._('Replace with', 'Admin')}</span>
                            </MatchHeader>
                            <Rows>
                                {filteredMaterialList.map((mat) => (
                                    <MatchRow key={mat.id} $selected={!!materialMap[mat.id]}>
                                        <Current>
                                            <Icon>
                                                <MaterialIcon />
                                            </Icon>
                                            <SelectionItemName title={mat.name}>{mat.name}</SelectionItemName>
                                        </Current>
                                        <Arrow aria-hidden='true'>
                                            <ArrowIcon />
                                        </Arrow>
                                        <AdvancedSelect
                                            aria-label={T._(`Replace ${mat.name}`, 'Admin')}
                                            options={sceneMaterialList.filter((m) => m.id !== mat.id).map((m) => ({ label: m.name, value: m.id }))}
                                            placeholder={T._('Leave as it is', 'Admin')}
                                            isClearable
                                            menuPosition='fixed'
                                            onChange={(sel) => setMatch(setMaterialMap, mat.id, sel?.value)}
                                        />
                                    </MatchRow>
                                ))}
                            </Rows>
                        </Group>
                    )}
                </Stack>
            ) : (
                <EmptyState>
                    <Icon>
                        <ObjectIcon />
                    </Icon>
                    <p>{T._('The actions of this selection do not point at any object or material, so there is nothing to match.', 'Admin')}</p>
                </EmptyState>
            )}
        </Dialog>
    );
};

export default ComposerRetargetAttributeDialog;
